import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, ShieldCheck, TreePine, Route, Zap, Droplets } from "lucide-react";
import api from "@/lib/api";
import PlotCard from "@/components/PlotCard";
import LeadForm from "@/components/LeadForm";
import { COMPANY } from "@/lib/api";

const HERO_IMG = "/hero_photo.jpg";

export default function Home() {
  const [plots, setPlots] = useState([]);

  useEffect(() => {
    api.get("/plots", { params: { featured: true } }).then((r) => setPlots(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0a0a0b] text-white">
      {/* HERO */}
      <section className="relative min-h-screen overflow-hidden grain" data-testid="home-hero">
        <img
          src={HERO_IMG}
          alt="Shree Aryavarta Developers — gated community entrance"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "left center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0b]/55 via-[#0a0a0b]/35 to-[#0a0a0b]" />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0a0a0b]/90 via-[#0a0a0b]/45 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-40 pb-24 min-h-screen flex flex-col justify-end items-end">
          <div className="max-w-2xl text-right ml-auto">
            <p className="label-overline rise">Launching Soon · Shujalpur, MP</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight mt-6 rise rise-delay-1">
              Where heritage<br />
              <span className="text-[#c5a059] italic">land</span> meets the future.
            </h1>
            <p className="text-[#cfcfd2] text-base md:text-lg mt-8 max-w-xl ml-auto leading-relaxed rise rise-delay-2">
              A new chapter in plotted development by {COMPANY} — gated layouts,
              wide tree-lined avenues, and clear titles in the heart of Madhya Pradesh.
              Launching soon.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4 rise rise-delay-3 justify-end">
              <Link
                to="/projects"
                data-testid="hero-cta-projects"
                className="btn-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
              >
                Explore the Layout <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-visit"
                className="btn-outline-gold inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-medium"
              >
                Get Launch Updates
              </Link>
            </div>
          </div>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#1f1f22] border border-[#1f1f22] rounded-xl overflow-hidden rise rise-delay-4">
            {[
              ["Phase 1", "Launching Soon"],
              ["Limited", "Inaugural Inventory"],
              ["50 ft", "Wide Main Road"],
              ["100%", "Clear Title Land"],
            ].map(([n, l]) => (
              <div key={l} className="bg-[#0a0a0b]/80 backdrop-blur p-6 lg:p-8">
                <p className="font-serif text-3xl md:text-4xl text-[#c5a059]">{n}</p>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#8f8f94] mt-2">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section className="border-y border-[#1f1f22] py-6 bg-[#08080a]">
        <div className="marquee">
          <div className="marquee-track">
            {Array(2).fill(null).flatMap((_, i) =>
              ["Launching Soon", "Clear Titles", "Gated Layout", "50 ft Main Road", "24×7 Security", "Underground Utilities", "Park & Greenery", "Transparent Pricing"].map((t, j) => (
                <span key={`${i}-${j}`} className="font-serif text-xl text-[#cfcfd2]">
                  {t} <span className="text-[#c5a059] mx-6">✦</span>
                </span>
              ))
            )}
          </div>
        </div>
      </section>

      {/* INTRO / VISION */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="label-overline">Our Vision</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-5 tracking-tight">
            Plotted living, <br />
            <span className="italic text-[#c5a059]">re-imagined</span> for Shujalpur.
          </h2>
        </div>
        <div className="lg:col-span-7 lg:pt-6">
          <p className="text-[#cfcfd2] text-base md:text-lg leading-relaxed">
            Shujalpur sits at a fortunate crossroad — fertile land, growing connectivity,
            and a community ready for thoughtful development. At {COMPANY}, we are launching
            our inaugural plotted layout with one promise: every plot is treated as the
            foundation of a family's legacy.
          </p>
          <p className="text-[#8f8f94] text-base leading-relaxed mt-5">
            From precisely demarcated boundaries to landscaped avenues and underground utilities,
            our debut project translates local insight into a contemporary residential experience —
            built ground-up for the next generation of homeowners.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[#1f1f22]">
            {[["New", "Venture · 2026"], ["Launching", "Soon"], ["Phase 1", "Inaugural Layout"]].map(([n, l]) => (
              <div key={l} className="bg-[#0a0a0b] p-6">
                <p className="font-serif text-3xl text-[#c5a059]">{n}</p>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#8f8f94] mt-2">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PLOTS — Tetris/Bento */}
      <section className="bg-[#08080a] border-y border-[#1f1f22] py-24 lg:py-32" data-testid="home-featured-plots">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <p className="label-overline">Inaugural Layout · Launching Soon</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight">A glimpse of the plots being prepared.</h2>
            </div>
            <Link
              to="/projects"
              data-testid="home-view-all-plots"
              className="text-sm text-[#c5a059] inline-flex items-center gap-2 hover:gap-3 transition-all"
            >
              View all inventory <ArrowUpRight size={16} />
            </Link>
          </div>

          {plots.length === 0 ? (
            <p className="text-[#8f8f94]">Loading featured plots…</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {plots.slice(0, 3).map((p) => (
                <PlotCard key={p.id} plot={p} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* WHY US — Bento */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
        <div className="mb-14">
          <p className="label-overline">Why Aryavarta</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 tracking-tight max-w-3xl">A new venture, built on uncompromising fundamentals.</h2>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          {[
            { icon: ShieldCheck, title: "Clear Titles", desc: "Every plot launched will come with a clear, verified title and full documentation support.", span: "lg:col-span-5" },
            { icon: TreePine, title: "Landscaped Greens", desc: "30%+ of our layout is reserved for parks, avenues and community greenery — built in from day one.", span: "lg:col-span-4" },
            { icon: Route, title: "50 ft Wide Main Road", desc: "A grand 50-ft main approach road with proper drainage, kerbs and street lighting.", span: "lg:col-span-3" },
            { icon: Zap, title: "Underground Electrification", desc: "Buried utilities planned across the layout for an uncluttered skyline.", span: "lg:col-span-4" },
            { icon: Droplets, title: "Assured Water Supply", desc: "Tubewell + overhead tank network with metered individual connections at handover.", span: "lg:col-span-4" },
            { icon: Building2, title: "Construction-Ready", desc: "Approved layouts so you can begin construction the day after registry.", span: "lg:col-span-4" },
          ].map(({ icon: Icon, title, desc, span }) => (
            <div
              key={title}
              className={`${span} card-hover bg-[#0f0f11] border border-[#2a2a2d] rounded-xl p-8`}
            >
              <Icon size={26} className="text-[#c5a059]" strokeWidth={1.4} />
              <h3 className="font-serif text-2xl mt-5">{title}</h3>
              <p className="text-sm text-[#8f8f94] mt-3 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LEAD FORM SECTION */}
      <section className="bg-[#08080a] border-t border-[#1f1f22] py-24 lg:py-32" data-testid="home-lead-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-overline">Stay In The Loop</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-5 tracking-tight">
              Be the first to know <span className="italic text-[#c5a059]">when we launch</span>.
            </h2>
            <p className="text-[#8f8f94] text-base mt-6 max-w-md leading-relaxed">
              Share your details and we'll keep you updated on the launch date, layout details,
              and indicative pricing — no booking commitment, just early information.
            </p>
            <div className="mt-10 space-y-3 text-sm text-[#cfcfd2]">
              <p>📞 +91 82240 00921 · +91 82240 00902 · +91 98265 59347</p>
              <p>📍 Site Office: Freeganj, Ashta Road, Shujalpur</p>
            </div>
          </div>
          <div className="lg:col-span-7">
            <LeadForm />
          </div>
        </div>
      </section>
    </div>
  );
}
